import React from 'react';
import { View, Text, StyleSheet, type StyleProp, type ViewStyle } from 'react-native';
import { colors } from '../styles/theme';
import { MESSAGING_APPS, type AutoSyncProvider } from '../types/connections';
import { MessagingAppIcon } from './MessagingAppIcon';

/** One row per messenger, the same on the Mac and the web: who it is, how it is, what to do. */
export function MessagingConnectionCard({
  provider,
  status,
  tone = 'neutral',
  detail,
  children,
  action,
  style,
}: {
  provider: AutoSyncProvider;
  status: string;
  tone?: 'neutral' | 'success' | 'warning';
  /** Defaults to what connecting the app does. */
  detail?: string;
  children?: React.ReactNode;
  action?: React.ReactNode;
  style?: StyleProp<ViewStyle>;
}) {
  const app = MESSAGING_APPS[provider];
  return (
    <View style={[styles.card, style]} testID={`connect-${provider}`}>
      <View style={styles.row}>
        <View style={styles.icon}>
          <MessagingAppIcon provider={provider} />
        </View>
        <View style={styles.copy}>
          <View style={styles.titleRow}>
            <Text accessibilityRole="header" style={styles.name}>
              {app.label}
            </Text>
            <View style={styles.status}>
              <View
                style={[
                  styles.dot,
                  tone === 'success' && styles.successDot,
                  tone === 'warning' && styles.warningDot,
                ]}
              />
              <Text style={[styles.caption, tone === 'warning' && styles.warning]}>{status}</Text>
            </View>
          </View>
          {tone === 'warning' ? (
            // A problem is read out when it appears and is never cut short.
            <View accessibilityRole="alert">
              <Text style={[styles.detail, styles.warning]}>{detail ?? app.description}</Text>
            </View>
          ) : (
            <Text style={styles.detail} numberOfLines={2}>
              {detail ?? app.description}
            </Text>
          )}
        </View>
        {action}
      </View>
      {children}
    </View>
  );
}
const styles = StyleSheet.create({
  card: {
    minWidth: 0,
    paddingVertical: 14,
    paddingHorizontal: 16,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: colors.surfaceLight,
    backgroundColor: colors.surface,
    gap: 10,
  },
  row: { flexDirection: 'row', alignItems: 'center', gap: 12 },
  icon: {
    width: 36,
    height: 36,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.background,
  },
  copy: { flex: 1, minWidth: 0, gap: 3 },
  titleRow: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', columnGap: 10 },
  name: { color: colors.text, fontSize: 15, fontWeight: '600' },
  status: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  caption: { color: colors.textSecondary, fontSize: 12, lineHeight: 18 },
  detail: { color: colors.textSecondary, fontSize: 12, lineHeight: 18 },
  dot: { width: 6, height: 6, borderRadius: 3, backgroundColor: colors.textMuted },
  successDot: { backgroundColor: colors.success },
  warningDot: { backgroundColor: colors.warning },
  warning: { color: colors.warning },
});
